/**
 * Phase 6 cross-cutting PR 5 — `<WorkspaceBadge>`.
 *
 * Wide+ (≥ 1280 px) chrome element: shows the active team name above
 * the SideNav's nav items. Earns the Wide+ bandwidth by surfacing
 * "where am I?" context that's invisible at Default and below
 * (today's daughter-facing flow runs solo so the badge is null;
 * trainer-managed athletes will see "Spring 2026 XC" or similar).
 *
 * Sourced from:
 *   - HANDOFF.md PR 5 acceptance: "Wide (1280–1919) + Ultra mount a
 *     <WorkspaceBadge> (team name) above the nav items in the
 *     SideNav."
 *   - direction.md §3 (sidenav layout pattern).
 *
 * Self-gates on:
 *   - viewport ≥ 1280 (Wide + Ultra only — hidden at Comfortable /
 *     Default / Mobile via CSS)
 *   - `team` resolved from `useTeamHub` (returns null when the user
 *     has no team — solo athletes see no badge)
 *
 * Why a separate component (not inline in SideNav):
 *   - SideNav is shared by every desktop route; the badge needs the
 *     team fetch which most routes don't otherwise need
 *   - The data fetch is async — embedding inside SideNav would couple
 *     navigation rendering to team data loading
 *   - Component-level Wide+ media query keeps the gate self-contained
 */

import { useTeamHub } from '../../hooks/useTeamHub';
import styles from './WorkspaceBadge.module.css';

export function WorkspaceBadge() {
  const { team, loading } = useTeamHub();

  // Loading → render nothing (no skeleton; the badge is decorative
  // chrome, a flash-of-empty-then-populated would be more distracting
  // than a delayed appearance).
  if (loading) return null;

  // No team → render nothing. Solo athletes have no workspace to
  // badge. Component mounts; CSS @media decides visibility.
  if (!team) return null;

  // G7 (UX-S3): label the badge "Team" + show name + sport, and DROP the
  // free-text `team.season` string — it collided with the athlete's training
  // season ("Spring 2026" beside "Training Season 2026" read as two seasons).
  return (
    <div className={styles.badge} aria-label={`Team: ${team.name}`}>
      <div className={styles.dot} aria-hidden="true" />
      <div className={styles.text}>
        <div className={styles.kicker}>Team</div>
        <div className={styles.name}>{team.name}</div>
        {team.sport ? <div className={styles.sport}>{team.sport}</div> : null}
      </div>
    </div>
  );
}
